/**
 * Card selection for the picked day in the "Jetzt passend" section.
 *
 * Only places that are actually open that day survive the filter (a missing or
 * unreadable opening time counts as closed), and the weather fit of that day
 * moves the fitting places up before the previous order decides.
 */
import { getDayOpeningHours } from '@/components/utils/useOpeningHours';
import { isInCurrentSeason, getSeasonBoost } from '@/components/utils/activityScoring';

const INDOOR = ['indoor', 'drinnen'];
const OUTDOOR = ['outdoor', 'draussen'];
const SWIM = ['pool', 'wasserrutsche', 'wasserspiele', 'strudel', 'sprungbrett', 'schwimmerbahn', 'badi', 'baden'];

/** 'bath' | 'indoor' | 'outdoor' | null — drives the weather-fit ordering. */
export function activityWeatherType(activity) {
  if ((activity.ausstattung || []).some((item) => SWIM.includes(item))) return 'bath';
  const filters = activity.weitere_filter || [];
  if (filters.some((f) => OUTDOOR.includes(f))) return 'outdoor';
  if (filters.some((f) => INDOOR.includes(f))) return 'indoor';
  return null;
}

/**
 * 2 = fits the day, 1 = no indoor/outdoor info (stays neutral),
 * 0 = works against the day. Mild weather keeps the previous order for all.
 */
export function weatherFitRank(activity, weather) {
  if (!weather?.condition || weather.temperature == null) return 0;

  const type = activityWeatherType(activity);
  const rough = weather.condition === 'rainy' || weather.condition === 'snowy'
    || weather.temperature <= 6 || weather.temperature >= 27;
  const sunnyAndWarm = weather.condition === 'sunny' && weather.temperature >= 18;

  if (rough) return type === 'indoor' ? 2 : type ? 0 : 1;
  if (sunnyAndWarm) return type === 'outdoor' || type === 'bath' ? 2 : type ? 0 : 1;
  return 0;
}

export function selectDayActivities(activities, { weekdayIndex, weather, seasons, limit = 8 }) {
  return activities
    .filter((activity) => isInCurrentSeason(activity, seasons))
    .filter((activity) => getDayOpeningHours(activity.opening_hours, weekdayIndex, activity.always_open) !== null)
    .sort((a, b) => {
      const fit = weatherFitRank(b, weather) - weatherFitRank(a, weather);
      if (fit !== 0) return fit;
      // Previous order: season boost dominates, popularity only breaks ties.
      const scoreA = getSeasonBoost(a, seasons) * 1000 + Math.sqrt(a.popularity_score || 0);
      const scoreB = getSeasonBoost(b, seasons) * 1000 + Math.sqrt(b.popularity_score || 0);
      return scoreB - scoreA;
    })
    .slice(0, limit);
}